import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, Loader2, MapPin, Sparkles, Users, X } from 'lucide-react';
import { LemitCota, ProspeccaoJobTipo } from '../../../types';
import { getCotaLemit, getJanelaNoturna, noitesEstimadas } from '../../../src/services/prospeccaoFila';

// ─────────────────────────────────────────────────────────────────────────────
// Agendar um lote da Lista Fria.
//
// Os botões da barra consultam AGORA. Este modal é o caminho de quem quer
// escolher: qual ação, a que horas, e quanto gastar por noite — porque o teto da
// Lemit é diário e um lote de 9 mil documentos leva semanas.
// ─────────────────────────────────────────────────────────────────────────────

export interface AgendamentoConfig {
    tipo: ProspeccaoJobTipo;
    janelaInicio: string;
    janelaFim: string;
    /** null = vai até a cota da conta acabar. */
    maxPorDia: number | null;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Quantos alvos cada ação tem no filtro/seleção atual. */
    disponiveis: Record<ProspeccaoJobTipo, number>;
    custoPessoa: number | null;
    custoEmpresa: number | null;
    salvando: boolean;
    onConfirm: (config: AgendamentoConfig) => void;
}

const ACOES: Array<{ tipo: ProspeccaoJobTipo; label: string; desc: string; icone: React.ReactNode; pago: 'lemit' | 'google' }> = [
    { tipo: 'lemit_documento', label: 'Enriquecer documento', desc: 'CPF traz a pessoa · CNPJ traz a empresa e o quadro societário', icone: <Sparkles size={15} />, pago: 'lemit' },
    { tipo: 'lemit_dono', label: 'Buscar CPF do dono', desc: 'Consulta o sócio escolhido e traz o WhatsApp dele', icone: <Users size={15} />, pago: 'lemit' },
    { tipo: 'geocode', label: 'Localizar no mapa', desc: 'Geocodifica pelo Google (~US$ 0,005 por endereço)', icone: <MapPin size={15} />, pago: 'google' },
];

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AgendarLoteModal: React.FC<Props> = ({ isOpen, onClose, disponiveis, custoPessoa, custoEmpresa, salvando, onConfirm }) => {
    const [tipo, setTipo] = useState<ProspeccaoJobTipo>('lemit_documento');
    const [inicio, setInicio] = useState('22:00');
    const [fim, setFim] = useState('23:59');
    const [limitar, setLimitar] = useState(false);
    const [maxPorDia, setMaxPorDia] = useState('200');
    const [cota, setCota] = useState<LemitCota | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        getJanelaNoturna().then((j) => { setInicio(j.inicio); setFim(j.fim); }).catch(() => { /* mantém o padrão */ });
        getCotaLemit().then(setCota).catch(() => setCota(null));
        // Abre já na primeira ação que tem alvo.
        const primeira = ACOES.find((a) => disponiveis[a.tipo] > 0);
        if (primeira) setTipo(primeira.tipo);
    }, [isOpen]);

    const qtd = disponiveis[tipo] || 0;
    const ehLemit = tipo !== 'geocode';
    const tetoNoite = limitar ? Math.max(1, parseInt(maxPorDia, 10) || 1) : (cota?.limite ?? 500);

    const resumo = useMemo(() => {
        if (qtd === 0) return null;
        if (!ehLemit) {
            return {
                custo: `~US$ ${(qtd * 0.005).toFixed(2)}`,
                ritmo: 'Geocode não tem teto diário — sai tudo de uma vez, em poucos minutos.',
                noites: 1,
            };
        }
        const unit = tipo === 'lemit_dono' ? custoPessoa : custoEmpresa;
        const noites = noitesEstimadas(qtd, tetoNoite);
        return {
            custo: unit != null ? fmtBRL(unit * qtd) : null,
            ritmo: limitar
                ? `${tetoNoite}/noite (teto que você definiu)`
                : `até ${tetoNoite}/noite (o que sobrar da cota da conta)`,
            noites,
        };
    }, [qtd, ehLemit, tipo, custoPessoa, custoEmpresa, tetoNoite, limitar]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"><CalendarClock size={18} /></div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white">Agendar lote</h3>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Roda sozinho no servidor, no horário que você escolher. Não precisa deixar o sistema aberto.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16} /></button>
                </div>

                <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
                    {/* Ação */}
                    <div className="space-y-1.5">
                        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">O que agendar</p>
                        {ACOES.map((a) => {
                            const n = disponiveis[a.tipo] || 0;
                            const ativo = tipo === a.tipo;
                            return (
                                <button key={a.tipo} onClick={() => setTipo(a.tipo)} disabled={n === 0}
                                    className={`w-full flex items-start gap-2.5 p-2.5 rounded-xl border text-left transition-all disabled:opacity-40 ${ativo
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
                                        : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                                    <span className={`mt-0.5 ${ativo ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`}>{a.icone}</span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">{a.label}</span>
                                        <span className="block text-[11px] text-slate-500 dark:text-slate-400">{a.desc}</span>
                                    </span>
                                    <span className={`text-xs font-bold shrink-0 ${n === 0 ? 'text-slate-300' : ativo ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500'}`}>
                                        {n.toLocaleString('pt-BR')}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Horário */}
                    <div>
                        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">Que horas rodar</p>
                        <div className="flex items-center gap-2">
                            <input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/25" />
                            <span className="text-xs text-slate-400">até</span>
                            <input type="time" value={fim} onChange={(e) => setFim(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/25" />
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                            Horário de Brasília. Se a faixa passar da meia-noite (ex.: 22:00 até 05:00), o lote continua madrugada adentro.
                        </p>
                    </div>

                    {/* Quanto gastar */}
                    {ehLemit && (
                        <div>
                            <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">Quanto consultar por noite</p>
                            <div className="space-y-1.5">
                                <button onClick={() => setLimitar(false)}
                                    className={`w-full flex items-start gap-2.5 p-2.5 rounded-xl border text-left transition-all ${!limitar
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
                                        : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700'}`}>
                                    <span className="flex-1">
                                        <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">Até a cota do dia acabar</span>
                                        <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                            Usa toda a sobra do teto da conta
                                            {cota ? ` (hoje: ${cota.usados}/${cota.limite} usados, sobram ${cota.restante})` : ''}.
                                        </span>
                                    </span>
                                </button>
                                <button onClick={() => setLimitar(true)}
                                    className={`w-full flex items-start gap-2.5 p-2.5 rounded-xl border text-left transition-all ${limitar
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
                                        : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700'}`}>
                                    <span className="flex-1">
                                        <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">No máximo…</span>
                                        <span className="block text-[11px] text-slate-500 dark:text-slate-400">Guarda o resto da cota para o time usar no dia seguinte.</span>
                                    </span>
                                </button>
                                {limitar && (
                                    <div className="flex items-center gap-2 pl-2.5">
                                        <input type="number" min="1" step="10" value={maxPorDia} onChange={(e) => setMaxPorDia(e.target.value)}
                                            className="w-28 px-3 py-1.5 text-sm rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/25" />
                                        <span className="text-xs text-slate-500 dark:text-slate-400">consultas por noite</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Resumo */}
                    {qtd === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-3">Nenhum alvo para esta ação no filtro atual.</p>
                    ) : resumo && (
                        <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 space-y-1">
                            <p className="text-xs font-bold text-slate-800 dark:text-slate-100">
                                {qtd.toLocaleString('pt-BR')} item(ns) · {resumo.ritmo}
                            </p>
                            {ehLemit && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Previsão: <b>{resumo.noites} noite(s)</b> até terminar, começando às {inicio}.
                                </p>
                            )}
                            {resumo.custo && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Custo total estimado: <b>{resumo.custo}</b>{ehLemit ? ' (cache de 24h não cobra)' : ''}.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-800">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-bold rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">Cancelar</button>
                    <button
                        onClick={() => onConfirm({ tipo, janelaInicio: inicio, janelaFim: fim, maxPorDia: limitar && ehLemit ? tetoNoite : null })}
                        disabled={qtd === 0 || salvando}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                    >
                        {salvando ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
                        Agendar para {inicio}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AgendarLoteModal;
